import { phoneRegex, emailRegex } from 'core/helpers/regex'
import i18n from 'core/services/i18n'
import type { WidgetElement } from 'embed_v2/types/version'
import type Viewer from 'embed_v2/types/viewer'

type RuleFn = (value?: any) => { error: string } | undefined

export const validationErrors = (error: unknown): Record<string, string> => {
  const data = (error as ValidationError)?.data

  if (!Array.isArray(data)) return {}

  return data.reduce(
    (acc: Record<string, string>, item: { field: string; error: string }) => ({ ...acc, [item.field]: item.error }),
    {}
  )
}

export class ValidationError extends Error {
  data: object

  constructor(data: object) {
    super()
    this.message = 'Validation Error'
    this.data = data
  }
}

class Validation {
  shape: Record<number, Record<string, RuleFn>>

  constructor() {
    this.shape = {}
  }

  get requiredMessage() {
    return i18n.t('validations.required_field')
  }

  appendRule(stepId: number, field: string | undefined, rule: RuleFn) {
    this.shape[stepId] = this.shape[stepId] || {}
    if (field) {
      this.shape[stepId][field] = rule
    }
  }

  add(stepId: number, element: WidgetElement) {
    switch (element.type) {
      case 'Widget::ElementQuiz':
      case 'Widget::ElementDropdown': {
        const rule: RuleFn = value => {
          if (element.options.required && !value) {
            return { error: this.requiredMessage }
          }
        }
        this.appendRule(stepId, element.options.key, rule)
        break
      }
      case 'Widget::ElementDatetime': {
        const rule: RuleFn = value => {
          if (!value) return { error: this.requiredMessage }
        }
        this.appendRule(stepId, 'date_time', rule)
        break
      }
      case 'Widget::ElementInput': {
        switch (element.options.type) {
          case 'email': {
            const rule: RuleFn = value => {
              if (!value?.trim().length) return { error: this.requiredMessage }
              if (!value.match(emailRegex)) return { error: i18n.t('validations.email') }
            }
            this.appendRule(stepId, 'email', rule)
            break
          }
          case 'phone': {
            const rule: RuleFn = value => {
              if (element.options.required && !value?.trim().length) return { error: this.requiredMessage }
              if (value?.trim().length && !value.match(phoneRegex)) return { error: i18n.t('validations.phone') }
            }
            this.appendRule(stepId, 'phone', rule)
            break
          }
          case 'name': {
            const rule: RuleFn = value => {
              if (element.options.required && !value?.trim().length) return { error: this.requiredMessage }
            }
            this.appendRule(stepId, 'name', rule)
            break
          }
          case 'custom': {
            if (element.options.required) {
              const rule: RuleFn = value => {
                if (!value?.trim().length) return { error: this.requiredMessage }
              }
              this.appendRule(stepId, element.options.key, rule)
            }
            break
          }
          default:
            break
        }
        break
      }
      case 'Widget::ElementTextarea': {
        if (element.options.required) {
          const rule: RuleFn = value => {
            if (!value?.trim().length) return { error: this.requiredMessage }
          }
          this.appendRule(stepId, element.options.key, rule)
        }
        break
      }
      case 'Widget::ElementCheckbox': {
        if (element.options.required) {
          const rule: RuleFn = value => {
            if (!value) return { error: this.requiredMessage }
          }
          this.appendRule(stepId, element.options.key, rule)
        }
        break
      }
      default:
        break
    }
  }

  validate(stepId: number, viewer: Viewer): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.shape[stepId]) resolve()

      const errors = []
      for (const [key, ruleFn] of Object.entries(this.shape[stepId])) {
        const value = viewer[key as keyof Viewer]
        const res = ruleFn(value)
        if (res) {
          errors.push({ field: key, error: res.error })
        }
      }

      if (errors.length) {
        reject(new ValidationError(errors))
      } else {
        resolve()
      }
    })
  }
}

export default Validation
